import React, { useEffect, useRef, useState } from 'react';
import {
  FaArrowRight, FaPray, FaGamepad, FaBookOpen, FaBirthdayCake, FaMusic, FaHeart,
  FaShieldAlt, FaUserCheck, FaIdBadge, FaFirstAid, FaChevronDown,
} from 'react-icons/fa';
import Footer from './Footer';
import './CityChurchKidsPage.css';
import img1 from '../Assets/Smile.jpg';
import img2 from '../Assets/bib.jpg';
import img3 from '../Assets/gifts.jpg';
import img4 from '../Assets/dance.jpg';
import img5 from '../Assets/sing.jpg';
import img6 from '../Assets/joy.jpg';
import heroImg from '../Assets/CityKidz.jpeg';

const WHAT_WE_DO = [
  { icon: <FaPray aria-hidden="true" />, title: 'We pray together', desc: 'Building a strong, personal connection with God from an early age.' },
  { icon: <FaGamepad aria-hidden="true" />, title: 'We play games', desc: 'Fun games that teach teamwork, kindness and God’s love.' },
  { icon: <FaBookOpen aria-hidden="true" />, title: 'We learn the Bible', desc: 'Interactive lessons that bring Scripture to life for every age.' },
  { icon: <FaBirthdayCake aria-hidden="true" />, title: 'We celebrate', desc: 'Birthdays, treats and little wins, so every child feels special.' },
  { icon: <FaMusic aria-hidden="true" />, title: 'We worship', desc: 'Singing and dancing that fills young hearts with joy and faith.' },
  { icon: <FaHeart aria-hidden="true" />, title: 'We belong', desc: 'A warm community where children make friends and feel at home.' },
];

const SAFETY = [
  { icon: <FaIdBadge aria-hidden="true" />, title: 'Secure check-in', desc: 'Every child is signed in and released only to a verified parent or guardian.' },
  { icon: <FaUserCheck aria-hidden="true" />, title: 'Trained volunteers', desc: 'Our team is screened, trained and passionate about caring for your children.' },
  { icon: <FaShieldAlt aria-hidden="true" />, title: 'Child protection', desc: 'Clear safeguarding procedures guide everything we do, every Sunday.' },
  { icon: <FaFirstAid aria-hidden="true" />, title: 'Ready for anything', desc: 'First-aid trained leaders and clear emergency plans in every room.' },
];

const GALLERY = [
  { image: img1, caption: 'Spreading joy with a bright smile.' },
  { image: img2, caption: 'Learning God’s word every Sunday.' },
  { image: img3, caption: 'Bringing Bible stories to life through creative play.' },
  { image: img4, caption: 'Dancing for joy, praising with every step.' },
  { image: img5, caption: 'Lifting our voices in song and celebration.' },
  { image: img6, caption: 'Arriving each week excited to learn and grow.' },
];

const FAQS = [
  { q: 'What ages do you cater for?', a: 'City Kids is designed for children aged 4 to 12, with age-appropriate groups so every child learns at the right level.' },
  { q: 'When do you meet?', a: 'City Kids runs during both of our Sunday services, so your children are cared for while you worship.' },
  { q: 'How do I register my child?', a: 'Simply arrive a few minutes early and our team will help you check in your child at the City Kids desk. It only takes a moment.' },
  { q: 'What happens on a first visit?', a: 'A leader will welcome you, take a few details, and show your child to their group. You will receive a matching tag so pick-up is safe and simple.' },
  { q: 'Is it safe?', a: 'Absolutely. We use secure check-in and check-out, trained and screened volunteers, and clear child-protection procedures throughout.' },
];

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const els = root.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return ref;
}

const CityChurchKidsPage = () => {
  const ref = useReveal();
  const [open, setOpen] = useState(0);

  return (
    <div ref={ref}>
      {/* HERO */}
      <header className="ck-hero" style={{ '--ck-hero-img': `url(${heroImg})` }}>
        <div className="tcc-container ck-hero__inner">
          <p className="tcc-eyebrow ck-hero__eyebrow">The Children&rsquo;s Ministry</p>
          <h1 className="ck-hero__title">Welcome to City Kids</h1>
          <p className="ck-hero__lead">
            A safe, joyful place where children aged 4 to 12 discover the love of Jesus,
            make friends and grow in faith, every single Sunday.
          </p>
          <div className="ck-hero__actions">
            <a className="tcc-btn tcc-btn--accent tcc-btn--lg" href="#ck-parents">
              Plan your first visit <FaArrowRight aria-hidden="true" />
            </a>
            <a className="tcc-btn tcc-btn--outline-on-dark tcc-btn--lg" href="#ck-gallery">
              See the fun
            </a>
          </div>
        </div>
      </header>

      {/* ABOUT */}
      <section className="tcc-section ck-about reveal" aria-labelledby="ck-about-h">
        <div className="tcc-container ck-about__grid">
          <div className="ck-about__media">
            <img src={img4} alt="Children dancing and celebrating at a City Kids Sunday session" loading="lazy" />
          </div>
          <div className="ck-about__text">
            <p className="tcc-eyebrow">Sunday school welcomes you</p>
            <h2 className="tcc-h2" id="ck-about-h">A strong foundation, built on joy</h2>
            <p>
              City Kids is a vibrant, engaging ministry for children aged 4 to 12. Everything
              we do is centred on sharing the love of Jesus Christ and helping children develop
              a strong spiritual foundation they will carry for life.
            </p>
            <ul className="ck-about__points">
              <li><strong>Ages 4&ndash;12</strong> in nurturing, age-appropriate groups</li>
              <li><strong>Every Sunday</strong> during both church services</li>
              <li><strong>Faith and fun</strong> woven into every lesson and activity</li>
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="tcc-section tcc-section--surface2 ck-do reveal" aria-labelledby="ck-do-h">
        <div className="tcc-container">
          <div className="ck-head">
            <p className="tcc-eyebrow">A typical Sunday</p>
            <h2 className="tcc-h2" id="ck-do-h">What we get up to</h2>
            <p className="tcc-lead">Every week is packed with worship, learning and plenty of laughter.</p>
          </div>
          <ul className="ck-do__grid">
            {WHAT_WE_DO.map((f) => (
              <li className="ck-do__card tcc-card tcc-card--hover" key={f.title}>
                <span className="ck-do__icon">{f.icon}</span>
                <h3 className="ck-do__title">{f.title}</h3>
                <p className="ck-do__desc">{f.desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SAFETY */}
      <section className="tcc-section ck-safety reveal" aria-labelledby="ck-safety-h">
        <div className="tcc-container">
          <div className="ck-head">
            <p className="tcc-eyebrow">Parents can relax</p>
            <h2 className="tcc-h2" id="ck-safety-h">Safety is our first priority</h2>
            <p className="tcc-lead">
              Before the fun begins, we make sure every child is known, cared for and secure.
            </p>
          </div>
          <ul className="ck-safety__grid">
            {SAFETY.map((s) => (
              <li className="ck-safety__card" key={s.title}>
                <span className="ck-safety__icon">{s.icon}</span>
                <div>
                  <h3 className="ck-safety__title">{s.title}</h3>
                  <p className="ck-safety__desc">{s.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* GALLERY */}
      <section id="ck-gallery" className="tcc-section tcc-section--surface2 ck-gallery reveal" aria-labelledby="ck-gallery-h" style={{ scrollMarginTop: '80px' }}>
        <div className="tcc-container">
          <div className="ck-head">
            <p className="tcc-eyebrow">Moments</p>
            <h2 className="tcc-h2" id="ck-gallery-h">Life at City Kids</h2>
            <p className="tcc-lead">A glimpse of the joy our children bring every Sunday.</p>
          </div>
          <ul className="ck-gallery__grid">
            {GALLERY.map((g, i) => (
              <li className={`ck-gallery__item ck-gallery__item--${i % 6}`} key={i}>
                <img src={g.image} alt="" loading="lazy" />
                <span className="ck-gallery__caption">{g.caption}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PARENT INFO / FAQ */}
      <section id="ck-parents" className="tcc-section ck-parents reveal" aria-labelledby="ck-parents-h" style={{ scrollMarginTop: '80px' }}>
        <div className="tcc-container ck-parents__grid">
          <div className="ck-parents__intro">
            <p className="tcc-eyebrow">Parent information</p>
            <h2 className="tcc-h2" id="ck-parents-h">Everything you need to know</h2>
            <p className="ck-parents__lead">
              First time with us? Here are the answers to the questions parents ask most.
              Still unsure about anything? Our team would love to help.
            </p>
            <a className="tcc-btn tcc-btn--primary tcc-btn--lg" href="/contact-us">
              Contact the children&rsquo;s team <FaArrowRight aria-hidden="true" />
            </a>
          </div>
          <ul className="ck-faq">
            {FAQS.map((f, i) => {
              const isOpen = open === i;
              return (
                <li className={`ck-faq__item ${isOpen ? 'is-open' : ''}`} key={f.q}>
                  <button
                    type="button"
                    className="ck-faq__q"
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                  >
                    <span>{f.q}</span>
                    <FaChevronDown aria-hidden="true" className="ck-faq__chev" />
                  </button>
                  <div className="ck-faq__a" hidden={!isOpen}>
                    <p>{f.a}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="ck-cta reveal" aria-labelledby="ck-cta-h">
        <div className="tcc-container ck-cta__inner">
          <h2 className="ck-cta__title" id="ck-cta-h">Join us this Sunday</h2>
          <p className="ck-cta__text">
            Bring your children along and watch them light up. We cannot wait to welcome
            your family into the City Kids community.
          </p>
          <div className="ck-cta__actions">
            <a className="tcc-btn tcc-btn--on-dark tcc-btn--lg" href="/service-times">
              See service times <FaArrowRight aria-hidden="true" />
            </a>
            <a className="tcc-btn tcc-btn--outline-on-dark tcc-btn--lg" href="/contact-us">
              Ask us a question
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CityChurchKidsPage;
